/**
 * The Watcher class triggers events if a input or form is (in)valid.
 *
 * Available events:
 * validate.input.valid
 * validate.input.invalid
 * validate.form.valid
 * validate.form.invalid
 * validate.form.reset
 *
 * @author Michel Heitbrink
 */
export default class Watcher {

    constructor(form, options) {

        if ( form instanceof HTMLElement ) {

            this.form = form;

            this.options = {
                trigger: 'blur', //submit, blur, keyup
                patterns: {
                    alpha : /^[a-zA-Z]+$/,
                    alpha_numeric : /^[a-zA-Z0-9]+$/,
                    integer : /^[-+]?\d+$/,
                    number : /^[-+]?\d*(?:[\.\,]\d+)?$/,
                    card : /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|(?:222[1-9]|2[3-6][0-9]{2}|27[0-1][0-9]|2720)[0-9]{12}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/,
                    cvv : /^([0-9]){3,4}$/,
                    email : /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,
                    url: /^((?:(https?|ftps?|file|ssh|sftp):\/\/|www\d{0,3}[.]|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\((?:[^\s()<>]+|(?:\([^\s()<>]+\)))*\))+(?:\((?:[^\s()<>]+|(?:\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:\'".,<>?\xab\xbb\u201c\u201d\u2018\u2019]))$/,
                    domain : /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,8}$/,
                    datetime : /^([0-2][0-9]{3})\-([0-1][0-9])\-([0-3][0-9])T([0-5][0-9])\:([0-5][0-9])\:([0-5][0-9])(Z|([\-\+]([0-1][0-9])\:00))$/,
                    date : /(?:19|20)[0-9]{2}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1[0-9]|2[0-9])|(?:(?!02)(?:0[1-9]|1[0-2])-(?:30))|(?:(?:0[13578]|1[02])-31))$/,
                    time : /^(0[0-9]|1[0-9]|2[0-3])(:[0-5][0-9]){2}$/,
                    dateISO : /^\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}$/,
                    month_day_year : /^(0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])[- \/.]\d{4}$/,
                    day_month_year : /^(0[1-9]|[12][0-9]|3[01])[- \/.](0[1-9]|1[012])[- \/.]\d{4}$/,
                    color : /^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/,
                },
                ...options
            }

            this.setEventReferences();
            this.setup();
        }
        else {
            console.error('@nutshell/core: Form isn\'t a DOM object');
        }
    }

    setEventReferences() {

        this.events = {
            reference: {
                inputs: {},
                submit: null,
                reset: null
            }
        };
    }

    /**
     * Fetch all inputs and event listeneres to the form en inputs
     */
    setup() {

        this.inputs = Array.from(this.form.querySelectorAll('input:not([type="submit"]), select, textarea'));

        this.form.addEventListener('submit', this.events.reference.submit = this.submit.bind(this));
        this.form.addEventListener('reset', this.events.reference.reset = this.reset.bind(this));

        if ( this.options.trigger == 'blur' || this.options.trigger == 'keyup' ) {

            /**
             * Add event changes to the input.
             * Change event will be used for checkboxes, radio inputs and selects
             */
            this.inputs.forEach( (input) => {

                let trigger = (
                        input.type == 'checkbox' ||
                        input.type == 'radio' ||
                        input.type == 'select' ||
                        input.type == 'select-one' ||
                        input.type == 'select-multiple') ? 'change' : this.options.trigger;

                if ( input.hasAttribute('data-validator-trigger') ) {
                    trigger = input.getAttribute('data-validator-trigger')
                }

                let uid = this.generateUid();
                input.dataset.reference = uid;
                input.addEventListener(trigger, this.events.reference.inputs[uid] = this.trigger.bind(this));
            });
        }
    }

    /**
     * Trigger a input event
     * @param  {Event} e
     */
    trigger(e) {

        let input = e.target;
        this.validateInput(input);
    }

    /**
     * Validate the form.
     * Use the silent var to validate the form without error messages
     *
     * @param  {boolean} silent
     * @return {boolean}
     */
    validate(silent) {

        let valid = true;

        if ( typeof silent != "boolean" ) {
            silent = false;
        }

        /**
         * Validate inputs
         */
        this.inputs.forEach( (input) => {

            if ( !this.validateInput(input, silent) ){
                valid = false;
            }
        });

        /**
         * Trigger events if the validation isn't silent
         */
        if ( !silent ){

            if ( valid ){
                this.event('validate.form.valid');
            }
            else {
                this.event('validate.form.invalid');
            }

            /**
            * Set data attribute valid
            */
            this.form.dataset.valid = valid;
        }


        return valid;
    }

    /**
     * Validate the given input
     *
     * @param  {HTMLElement} input
     * @param  {boolean} silent
     * @return {boolean}
     */
    validateInput(input, silent) {

        let errors = [];

        /**
         * Ignore inputs with data-ignore
         */
        if ( input.hasAttribute('data-ignore')
            || input.type == 'hidden'
            || input.disabled ) {

            return true;
        }

        if ( input.hasAttribute('data-silent') ) {
            silent = true;
        }

        /**
         * Check if the value is required and valid
         */
        switch ( input.type ) {

            case 'radio':
                    if ( !this.validateRadio(input) ) {
                        errors.push('required');
                    }
                break;

            case 'checkbox':
                    if ( !this.validateRequirement(input) ) {
                        errors.push('required');
                    }

                    if ( !this.validateCheckbox(input) ) {
                        errors.push('multiple');
                    }
                break;

            case 'select':
            case 'select-one':
            case 'select-multiple':
                    if ( !this.validateRequirement(input) ) {
                        errors.push('required');
                    }
                break;

            default:
                    if ( !this.validateRequirement(input) ) {
                        errors.push('required');
                    }

                    if ( !this.validateText(input) ) {
                        errors.push('pattern');
                    }
        }

        /**
         * Check if the input must be the same as the given dependecy
         */
        if ( input.hasAttribute('data-equalto') ) {

            let dependecy = this.inputs.find( i => i.id == input.getAttribute('data-equalto') );

            if ( dependecy ) {

                if ( dependecy.value != input.value ) {
                    errors.push('equalto');
                }
            }
            else {
                console.warn('@nutshell/core: data-equalto not found. Be sure the data-equalto is a id of a existing DOM element (without #).');
            }
        }

        /**
         * Trigger the custom validators
         */
        if ( input.hasAttribute('data-validator') ) {

            let validators = input.getAttribute('data-validator');

            if (typeof validators == 'string' ) {

                validators.split(' ').forEach( (v) => {

                    if ( this.options.custom.validators.hasOwnProperty(v) ){

                        if ( typeof this.options.custom.validators[v] == 'function' ) {
                            this.options.custom.validators[v](input) || errors.push(v);
                        }
                    }
                })
            }
        }

        /**
         * Check if the input has a custom validator function.
         * If so, trigger the function
         */
        if ( typeof input.validation == 'function' ) {
            input.validation(input) || errors.push('custom');
        }

        /**
         * Validate the length of the value.
         */
        if ( input.hasAttribute('maxlength') || input.hasAttribute('minlength') ) {

            let max = input.getAttribute('maxlength');
            let min = input.getAttribute('minlength');

            if ( max && max < input.value.length ) {
                errors.push('maxlength')
            }

            if ( min && min > input.value.length && input.value.length ) {
                errors.push('minlength')
            }

        }
        else if ( input.hasAttribute('min') || input.hasAttribute('max')){

            let max = input.getAttribute('max');
            let min = input.getAttribute('min');

            if ( input.type == 'number' ) {

                if ( max && max < parseFloat(input.value) ) {
                    errors.push('max');
                }

                if ( min && min > parseFloat(input.value) ) {
                    errors.push('min');
                }
            }
            else if ( input.type == 'date' ) {

                let date = new Date( input.value );

                if ( max && new Date(max) < date ) {
                    errors.push('max');
                }

                if ( min && new Date(min) < date ) {
                    errors.push('min');
                }
            }
        }

        /**
         * Trigger event if silent is false
         */
        if ( errors.length > 0 ) {
            this.eventOnInput(input, 'is-invalid', {errors});
        }
        else {
            this.eventOnInput(input, 'is-valid');
        }

        if ( !silent ) {

            if ( errors.length > 0 ) {
                this.event('validate.input.invalid', { input, errors });
            }
            else {
                this.event('validate.input.valid', { input });
            }
        }

        return errors.length == 0;
    }

    /**
     * Check if the Radio input is valid.
     * Radio input's only check is the requirement check
     *
     * @param  {HTMLElement} input
     * @return {boolean}
     */
    validateRadio(input) {
        return this.validateRequirement(input);
    }

    /**
     * Check if checkbox is checked.
     *
     * @param  {HTMLElement} input
     * @return {boolean}
     */
    validateCheckbox(input) {

        let checked = this.inputs.filter( i => i.name == input.name && i.checked );
        let minRequired = 0;

        this.inputs.forEach( (i) => {

            if ( i.hasAttribute('data-min-required') ) {

                let min = i.getAttribute('data-min-required');

                if ( !isNaN(min) && min > minRequired ) {
                    minRequired = min;
                }
            }
        });

        return ( checked.length >= minRequired );
    }

    /**
     * Validate text input based on pattern and type.
     * Patterns are stored in options.patterns
     *
     * Custom validators are only applied to inputs with the attribute `data-pattern` or `pattern`
     *
     * @param  {HTMLElement} input
     * @return {boolean}
     */
    validateText(input) {

        let pattern = ( input.getAttribute('data-pattern') || input.getAttribute('pattern') || input.type ),
            value = input.value;

        if ( value.length ) {

            if ( this.options.patterns.hasOwnProperty(pattern) ) {
                return this.options.patterns[pattern].test(value);
            }
            else if (
                ( input.hasAttribute('data-pattern') || input.hasAttribute('pattern') ) &&
                 this.options.custom.patterns.hasOwnProperty(pattern)
            ) {
                return this.options.custom.patterns[pattern].test(value);
            }
            else if ( pattern !== input.type ) {
                return new RegExp(pattern).test(value);
            }
        }

        return true;
    }

    /**
     * Check if input is required and filled
     *
     * @param  {HTMLElement} input
     * @return {boolean}
     */
    validateRequirement(input) {

        switch ( input.type ) {

            case 'radio':
            case 'checkbox':

                    let required = this.inputs.filter( i => i.name == input.name && i.required );
                    let checked = this.inputs.filter( i => i.name == input.name && i.checked );

                    return ( required.length > 0 ) ? ( checked.length > 0 ) : true;

                break;

            case 'select':
            case 'select-one':
            case 'select-multiple':

                    let option = input.selectedOptions.length > 0 ? input.selectedOptions[0] : undefined;

                    return ( ( input.required && option !== undefined && !this.isEmpty(option.value) )
                            || input.required == undefined
                            || input.required == false   );
                break;

            default:

                return ( ( input.required && !this.isEmpty(input.value) )
                            || input.required == undefined
                            || input.required == false   );
        }
    }

    /**
     * Handle the submit of the form and validate.
     *
     * @param  {Event} e
     */
    submit(e) {
        e.preventDefault();
        this.validate();
    }

    /**
     * Handle the reset of the form
     *
     * @param {Event} e
     */
    reset(e) {
        this.event('validation.form.reset');
    }

    /**
     * Trigger event on the form
     *
     * @param  {string} name
     * @param  {object} data
     */
    event(name, data) {

        let ev = new Event(name, {'bubbles':true, 'cancelable':false});

        if ( typeof data == 'object' ){

            Object.keys(data).forEach( (key) => {
                ev[key] = data[key];
            })
        }

        this.form.dispatchEvent(ev);
    }

    /**
     * Trigger event on given input
     *
     * @params {HTMLElement} input
     * @param  {string} name
     * @param  {object} data
     */
    eventOnInput(input, name, data) {

        let ev = new Event(name, {"bubbles":true, "cancelable":false});

        if ( typeof data == 'object' ){

            Object.keys(data).forEach( (key) => {
                ev[key] = data[key];
            })
        }
        
        input.dispatchEvent(ev);
    }

    /**
     * Update form. Used if the html has been changed
     */
    update() {
        this.destroy();
        this.setup();
    }

    /**
     * Remove all events listeneres
     */
    destroy() {

        delete this.form.valid;

        this.form.removeEventListener('submit', this.events.reference.submit);
        this.form.removeEventListener('reset', this.events.reference.reset);

        if ( this.options.trigger == 'blur' || this.options.trigger == 'keyup' ) {

            this.inputs.forEach( (input) => {

                let trigger = (
                        input.type == 'checkbox' ||
                        input.type == 'radio' ||
                        input.type == 'select' ||
                        input.type == 'select-one' ||
                        input.type == 'select-multiple') ? 'change' : this.options.trigger;

                if ( input.hasAttribute('data-validator-trigger') ) {
                    trigger = input.getAttribute('data-validator-trigger')
                }

                input.removeEventListener(trigger, this.events.reference.inputs[input.dataset.reference]);
            });
        }

        this.setEventReferences();
    }

    /**
     * Check if given value is empty
     *
     * @param  {string}  value
     * @return {Boolean}
     */
    isEmpty(value) {

        if ( typeof value == 'string' ) {
            return ( value.trim() == '' );
        }

        return false;
    }

    generateUid() {

        let randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';

        for ( var i = 0; i < 10; i++ ) {
            result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
        }

        return result;
    }
}
